// ============================================================
// EFFECT CHOICES - what the effect editor offers (`EffectList`): the kinds
// a bundle may hold with their labels, a blank effect of each, and the
// skills a bonus may be on. Split out of the stance edits when the stance
// bench went (plan modes-rework, stage 4).
//
// Pure. Zero React dependency.
// ============================================================
import { SKILL_TEST_DEFS } from '../characteristicTests';
import type { EffectKind, StanceEffect } from './effectItems';

type PlainKind = Exclude<EffectKind, 'exposure'>;

/** The kinds on offer, in the order the editor lists them. */
export const EFFECT_CHOICES: { kind: EffectKind; label: string }[] = [
  { kind: 'skill_bonus', label: 'A bonus to a skill' },
  { kind: 'damage_bonus', label: 'A bonus to damage' },
  { kind: 'called_shot_reduction', label: 'A lighter Called Shot penalty' },
  { kind: 'defence_grant', label: 'An extra Dodge or Parry' },
  { kind: 'initiative_modifier', label: 'A bonus to Initiative' },
  { kind: 'exposure', label: 'What attackers get against me' },
  { kind: 'attack_reroll', label: 'A re-roll of the attack or its damage' },
  // The sheet tier (plan armoury): they may wait only on a condition about oneself.
  { kind: 'characteristic_bonus', label: 'A bonus to a characteristic' },
  { kind: 'unnatural_characteristic', label: 'An Unnatural characteristic' },
  { kind: 'grant_talent', label: 'A talent' },
  { kind: 'grant_trait', label: 'A trait (and its immunities)' },
  { kind: 'wound_bonus', label: 'More wounds' },
  { kind: 'flat_ap_bonus', label: 'Armour everywhere' },
  { kind: 'resistance', label: 'A resistance' },
];

/** Where each plain kind starts. An exposure has no blank: it is picked (`AddExposureModal`). */
const BLANKS: { [K in PlainKind]: Extract<StanceEffect, { type: K }> } = {
  skill_bonus: { type: 'skill_bonus', skill: 'ws', value: 10 },
  damage_bonus: { type: 'damage_bonus', scope: 'all', value: 1 },
  called_shot_reduction: { type: 'called_shot_reduction', scope: 'all', value: 10 },
  defence_grant: { type: 'defence_grant', defence: 'dodge', additional: 1 },
  initiative_modifier: { type: 'initiative_modifier', value: 1 },
  attack_reroll: { type: 'attack_reroll', of: 'damage' },
  characteristic_bonus: { type: 'characteristic_bonus', characteristic: 'tgh', value: 10 },
  unnatural_characteristic: { type: 'unnatural_characteristic', characteristic: 'str', multiplier: 2 },
  grant_talent: { type: 'grant_talent', talentId: 'frenzy' },
  grant_trait: { type: 'grant_trait', traitId: 'from_beyond' },
  wound_bonus: { type: 'wound_bonus', value: 2 },
  flat_ap_bonus: { type: 'flat_ap_bonus', value: 2 },
  resistance: { type: 'resistance', value: 10, against: 'Fear' },
};

/** A new effect of a kind, ready to be adjusted. */
export const blankEffect = (kind: PlainKind): StanceEffect => ({ ...BLANKS[kind] });

/** What a skill bonus may be on: the two attack skills, then the catalog's. */
export const SKILL_CHOICES: { id: string; label: string }[] = [
  { id: 'ws', label: 'Weapon Skill' },
  { id: 'bs', label: 'Ballistic Skill' },
  ...SKILL_TEST_DEFS.map((s) => ({ id: s.id, label: s.label })),
];
